import { useDispatch, useSelector } from 'react-redux'
import { X } from 'lucide-react'
import { RootState } from '@/store/store'
import {
  removeTag,
  clearTags,
  addTag,
  setClassification,
  setHasFaces,
  clearFilters,
} from '@/store/slices/filterSlice'
import { useGetTagsQuery } from '@/store/api'

function FilterPanel() {
  const dispatch = useDispatch()
  const { activeTags, classification, hasFaces } = useSelector(
    (state: RootState) => state.filter
  )
  const { data: tags } = useGetTagsQuery()

  const hasActiveFilters = activeTags.length > 0 ||
    !classification.photos ||
    !classification.screenshots ||
    !classification.documents ||
    !classification.textHeavy ||
    hasFaces !== 'any'

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Active Filters */}
      {activeTags.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Active Filters</span>
            <button
              onClick={() => dispatch(clearTags())}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {activeTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded-full text-xs"
              >
                {tag}
                <button
                  onClick={() => dispatch(removeTag(tag))}
                  className="hover:text-primary-foreground"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-medium mb-3">Filter By</h3>

        {/* Tags */}
        <div className="mb-4">
          <div className="text-xs text-muted-foreground mb-2">Tags (click to filter)</div>
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {tags?.slice(0, 20).map(({ name, count }) => (
              <button
                key={name}
                onClick={() => dispatch(addTag(name))}
                disabled={activeTags.includes(name)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  activeTags.includes(name)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                {name} ({count})
              </button>
            ))}
            {!tags && (
              <span className="text-xs text-muted-foreground">Loading tags...</span>
            )}
          </div>
        </div>

        {/* Classification */}
        <div className="mb-4">
          <div className="text-xs text-muted-foreground mb-2">Classification</div>
          <div className="space-y-2">
            {[
              { key: 'photos', label: 'Photos' },
              { key: 'screenshots', label: 'Screenshots' },
              { key: 'documents', label: 'Documents' },
              { key: 'textHeavy', label: 'Text-heavy' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={classification[key as keyof typeof classification]}
                  onChange={(e) =>
                    dispatch(setClassification({ [key]: e.target.checked }))
                  }
                  className="w-4 h-4 rounded"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Has Faces */}
        <div className="mb-4">
          <div className="text-xs text-muted-foreground mb-2">Has Faces</div>
          <div className="flex gap-2">
            {[
              { value: 'any', label: 'Any' },
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ].map(({ value, label }) => (
              <label key={value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="hasFaces"
                  checked={hasFaces === value}
                  onChange={() => dispatch(setHasFaces(value as 'any' | 'yes' | 'no'))}
                  className="w-4 h-4"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Clear All Filters */}
        {hasActiveFilters && (
          <button
            onClick={() => dispatch(clearFilters())}
            className="w-full py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-secondary transition-colors"
          >
            Clear All Filters
          </button>
        )}
      </div>
    </div>
  )
}

export default FilterPanel
